import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import PageContainer from 'components/@layout/PageContainer';
import { ClassroomFilterValue } from 'components/ClassroomsModule/types';
import { useFormik } from 'formik';
import GET_CLASSROOMS_MANAGEMENT_QUERY from 'graph/classroom/get-classrooms-management-query';
import useClassroomActionOptionsMenuProps from 'hooks/use-classroom-action-options-menu';
import { flow } from 'lodash';
import { filter } from 'lodash/fp';
import { ModuleProps } from 'types';
import {
  Organization,
  User,
  UserClassroomEnrollment,
  Classroom,
  ProductType
} from 'types/schema-generated-types';
import useProductCache from 'store/cache/product-cache';
import FilterBar from './components/FilterBar';
import ListViewContainer from './components/ListViewContainer';
import HeaderBar from './HeaderBar';

const AdminClassesStudents: React.FC<ModuleProps> = props => {
  const [{ getPliProductsByType }] = useProductCache();
  const [headerLoader, setLoading] = React.useState(false);
  const { data, loading } = useQuery(GET_CLASSROOMS_MANAGEMENT_QUERY);

  const {
    actionOptions,
    archivedOptions,
    loading: classroomMutationLoading
  } = useClassroomActionOptionsMenuProps({
    translationId: props.id,
    classrooms: data?.organizations?.map(({ classrooms }: Organization) => classrooms ?? []).flat(),
    adminConsole: true
  });

  const { values, handleChange, resetForm } = useFormik({
    initialValues: {
      product: '',
      educator: '',
      search: '',
      classroomStatus: '',
      organization: ''
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: (): void => {}
  });

  // TODO: Might be worth reconsidering, This might be better filtered via
  // the GraphQL endpoint, however because we need to filter by both users
  // and organizations, fetching via the organizations makes more sense.
  const filteredClassrooms: Array<Classroom> = useMemo(() => {
    if (data && data.organizations) {
      let classrooms = [] as Classroom[];
      if (values.organization && values.organization !== '') {
        const organization = (data.organizations as Organization[]).find(
          org => org.id === Number(values.organization)
        );
        classrooms = organization?.classrooms || ([] as Classroom[]);
      } else {
        classrooms = data.organizations.reduce(
          (classrooms: Classroom[], organization: Organization) => {
            return [...classrooms, ...(organization.classrooms || [])];
          },
          []
        );
      }

      return flow(
        filter((classroom: Classroom) => {
          return !values.search
            ? true
            : classroom.name?.toLowerCase().includes(values.search?.toLowerCase());
        }),
        filter((classroom: Classroom) => {
          if (
            (values.classroomStatus === '' && !classroom.dateArchived) ||
            (values.classroomStatus === 'archive' && classroom.dateArchived) ||
            (values.classroomStatus === 'active' && !classroom.dateArchived)
          ) {
            return true;
          }

          return false;
        }),
        filter((classroom: Classroom) => {
          return values.educator === ''
            ? true
            : Boolean(
                classroom.users?.find(
                  ({ userId }: UserClassroomEnrollment) => userId === Number(values.educator)
                )
              );
        }),
        filter((classroom: Classroom) => {
          return values.product === ''
            ? true
            : Boolean(
                classroom.licenseUsage?.find(
                  licenseUsage => licenseUsage?.product?.code === values.product
                )
              );
        })
      )(classrooms);
    }

    return [];
  }, [data, values]);

  const currentOptions =
    values.classroomStatus === ClassroomFilterValue.ACTIVE || !values.classroomStatus
      ? actionOptions
      : archivedOptions;

  return (
    <PageContainer loading={loading || headerLoader || classroomMutationLoading}>
      <HeaderBar admin setLoading={setLoading} {...props} />
      <FilterBar
        values={values}
        handleChange={handleChange}
        resetForm={resetForm}
        organizations={data?.organizations as Organization[]}
        educators={data?.users as User[]}
        products={getPliProductsByType(ProductType.STUDENT)}
      />
      <ListViewContainer classrooms={filteredClassrooms} actionOptions={currentOptions} />
    </PageContainer>
  );
};

export default AdminClassesStudents;
